// ===================================
// COMPLETELY FIXED HEALTH CHECK COMPONENT - src/components/HealthCheck.tsx
// ===================================
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { HealthStatus } from '../types';
import { RefreshCw, CheckCircle, XCircle, AlertTriangle, Server, Database, Zap, FileText, Clock } from 'lucide-react';

const HealthCheck: React.FC = () => {
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchHealth = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/health');
      setHealth(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('❌ Failed to fetch health status:', error);
      setHealth(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status: string | boolean) => {
    if (status === 'healthy' || status === 'operational' || status === 'running' || status === 'accessible' || status === true) {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    }
    if (status === 'degraded') {
      return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    }
    return <XCircle className="h-5 w-5 text-red-500" />;
  };

  const getStatusText = (status: string | boolean) => {
    if (status === 'healthy' || status === 'operational' || status === 'running' || status === 'accessible' || status === true) {
      return 'OK';
    }
    if (status === 'degraded') {
      return 'Degraded';
    }
    return 'Error';
  };

  const getStatusColor = (status: string | boolean) => {
    if (status === 'healthy' || status === 'operational' || status === 'running' || status === 'accessible' || status === true) {
      return 'text-green-600';
    }
    if (status === 'degraded') {
      return 'text-yellow-600';
    }
    return 'text-red-600';
  };

  const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const badgeClasses = {
      healthy: 'bg-green-100 text-green-800',
      degraded: 'bg-yellow-100 text-yellow-800',
      unhealthy: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeClasses[status as keyof typeof badgeClasses] || badgeClasses.unhealthy}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  // Helper function to safely get user info
  const getSalesforceUserInfo = (user: any) => {
    if (typeof user === 'string') {
      return { name: user, username: user };
    }
    if (user && typeof user === 'object') {
      return {
        name: user.name || 'Unknown',
        username: user.username || user.id || 'Unknown'
      };
    }
    return { name: 'Unknown', username: 'Unknown' };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Health & Connectivity</h1>
          <p className="text-gray-600">
            Monitor the status of backend services and integrations
          </p>
        </div>
        <button
          onClick={fetchHealth}
          disabled={isLoading}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Overall Status */}
      {health && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {getStatusIcon(health.status)}
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">System Status</h3>
                <p className="text-sm text-gray-500">
                  {health.message} • Version {health.version} • {health.environment}
                </p>
              </div>
            </div>
            <div className="text-right">
              <StatusBadge status={health.status} />
              {lastUpdated && (
                <p className="text-xs text-gray-500 mt-1">
                  Last updated: {lastUpdated.toLocaleTimeString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Service Status Grid */}
      {health && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Backend API */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Server className="h-8 w-8 text-blue-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Backend API</p>
                  <div className={`text-sm flex items-center ${getStatusColor(true)}`}>
                    {getStatusIcon(true)}
                    <span className="ml-1">{getStatusText(true)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Database */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Database className="h-8 w-8 text-blue-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Database</p>
                  <div className={`text-sm flex items-center ${getStatusColor(health.database?.healthy || false)}`}>
                    {getStatusIcon(health.database?.healthy || false)}
                    <span className="ml-1">{getStatusText(health.database?.healthy || false)}</span>
                  </div>
                  {health.database?.message && (
                    <p className="text-xs text-gray-500 mt-1">({health.database.message})</p>
                  )}
                  {health.database?.error && (
                    <p className="text-xs text-red-500 mt-1">{health.database.error}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Salesforce */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Zap className="h-8 w-8 text-blue-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Salesforce</p>
                  <div className={`text-sm flex items-center ${getStatusColor(health.salesforce?.healthy || false)}`}>
                    {getStatusIcon(health.salesforce?.healthy || false)}
                    <span className="ml-1">{getStatusText(health.salesforce?.healthy || false)}</span>
                  </div>
                  {health.salesforce?.healthy && health.salesforce.flowDiscovery && (
                    <p className="text-xs text-gray-500 mt-1">
                      (Flows: {health.salesforce.flowDiscovery.count} via {health.salesforce.flowDiscovery.method})
                    </p>
                  )}
                  {health.salesforce?.error && (
                    <p className="text-xs text-red-500 mt-1">{health.salesforce.error}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Vector Extension */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Server className="h-8 w-8 text-blue-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Vector Extension</p>
                  <div className={`text-sm flex items-center ${getStatusColor(health.vector_extension?.available || false)}`}>
                    {getStatusIcon(health.vector_extension?.available || false)}
                    <span className="ml-1">{getStatusText(health.vector_extension?.available || false)}</span>
                  </div>
                  {health.vector_extension?.status && (
                    <p className="text-xs text-gray-500 mt-1">({health.vector_extension.status})</p>
                  )}
                  {health.vector_extension?.error && (
                    <p className="text-xs text-red-500 mt-1">{health.vector_extension.error}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Embedding Service */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Server className="h-8 w-8 text-blue-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Embedding Service</p>
                  <div className={`text-sm flex items-center ${getStatusColor(health.embedding_service?.available || false)}`}>
                    {getStatusIcon(health.embedding_service?.available || false)}
                    <span className="ml-1">{getStatusText(health.embedding_service?.available || false)}</span>
                  </div>
                  {health.embedding_service?.available && (
                    <p className="text-xs text-gray-500 mt-1">({health.embedding_service.model})</p>
                  )}
                  {health.embedding_service?.error && (
                    <p className="text-xs text-red-500 mt-1">{health.embedding_service.error}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* RAG Service */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Server className="h-8 w-8 text-blue-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">RAG Service</p>
                  <div className={`text-sm flex items-center ${getStatusColor(health.rag_service?.available || false)}`}>
                    {getStatusIcon(health.rag_service?.available || false)}
                    <span className="ml-1">{getStatusText(health.rag_service?.available || false)}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">(Operational)</p>
                  {health.rag_service?.error && (
                    <p className="text-xs text-red-500 mt-1">{health.rag_service.error}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Job Processor */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-blue-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Job Processor</p>
                  <div className={`text-sm flex items-center ${getStatusColor(health.job_processor?.status === 'running')}`}>
                    {getStatusIcon(health.job_processor?.status === 'running')}
                    <span className="ml-1">{getStatusText(health.job_processor?.status === 'running')}</span>
                  </div>
                  {health.job_processor?.pending_jobs !== undefined && (
                    <p className="text-xs text-gray-500 mt-1">
                      (Pending jobs: {health.job_processor.pending_jobs})
                    </p>
                  )}
                  {health.job_processor?.error && (
                    <p className="text-xs text-red-500 mt-1">{health.job_processor.error}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* File System */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FileText className="h-8 w-8 text-blue-500" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">File System</p>
                  <div className={`text-sm flex items-center ${getStatusColor(health.file_system?.uploads_directory === 'accessible')}`}>
                    {getStatusIcon(health.file_system?.uploads_directory === 'accessible')}
                    <span className="ml-1">{getStatusText(health.file_system?.uploads_directory === 'accessible')}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">({health.file_system?.uploads_directory})</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Salesforce Info */}
      {health?.salesforce?.healthy && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Salesforce Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">API Version</p>
              <p className="text-sm text-gray-900">{health.salesforce.apiVersion || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Base URL</p>
              <p className="text-sm text-gray-900 truncate">{health.salesforce.baseUrl || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">User</p>
              {health.salesforce.user ? (
                <>
                  <p className="text-sm text-gray-900">{getSalesforceUserInfo(health.salesforce.user).name}</p>
                  <p className="text-xs text-gray-500">{getSalesforceUserInfo(health.salesforce.user).username}</p>
                </>
              ) : (
                <p className="text-sm text-gray-500">N/A</p>
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Token Expiry</p>
              <p className="text-sm text-gray-900">
                {health.salesforce.tokenExpiry ? new Date(health.salesforce.tokenExpiry).toLocaleString() : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Flow Discovery</p>
              <p className="text-sm text-gray-900">
                {health.salesforce.flowDiscovery?.count || 0} flows via {health.salesforce.flowDiscovery?.method || 'unknown'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Available Features */}
      {health?.features && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Available Features</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Object.entries(health.features).map(([feature, enabled]) => (
              <div key={feature} className="flex items-center">
                {getStatusIcon(enabled)}
                <span className="ml-2 text-sm text-gray-900 capitalize">
                  {feature.replace(/_/g, ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && !health && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading health status...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {!isLoading && !health && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to fetch health status</h3>
            <p className="text-gray-600 mb-4">
              Failed to connect to the backend API. Please check your connection and try again.
            </p>
            <button
              onClick={fetchHealth}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthCheck;